import { Avatar, Badge, Button, Card, CardContent } from '@bs/ui-components'
import { ArrowLeft, Calendar, ExternalLink, Link2, Lock, MapPin, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { TalentJobCard } from '@/features/jobs/components/talent-job-card'
import { companyLogoUrl } from '@/services/assets'
import { formatLocation, getCities, getCompanyBySlug, getIndustries, getLookup, labelFor, labelsFor, type CityItem, type CompanyDetail, type MetadataItem } from '@/services/metadata'
import {
  applyToJob,
  getApplications,
  getSavedJobs,
  saveJob,
  searchJobs,
  type Application,
  type JobSearchItem,
  type SavedJob,
} from '@/features/jobs/api'

type Tab = 'about' | 'jobs'

export function CompanyDetailPage() {
  const { companySlug } = useParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('about')

  const [realCompany, setRealCompany] = useState<CompanyDetail | null>(null)
  const [notFoundReal, setNotFoundReal] = useState(false)
  const [realJobs, setRealJobs] = useState<JobSearchItem[]>([])
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [industries, setIndustries] = useState<MetadataItem[]>([])
  const [companySizes, setCompanySizes] = useState<MetadataItem[]>([])
  const [workspaceTypes, setWorkspaceTypes] = useState<MetadataItem[]>([])
  const [businessModelLookup, setBusinessModelLookup] = useState<MetadataItem[]>([])
  const [companyMarketLookup, setCompanyMarketLookup] = useState<MetadataItem[]>([])
  const [businessStageLookup, setBusinessStageLookup] = useState<MetadataItem[]>([])
  const [citiesById, setCitiesById] = useState<Record<number, CityItem>>({})

  useEffect(() => {
    if (!companySlug) return
    let cancelled = false
    getCompanyBySlug(companySlug)
      .then(async (company) => {
        if (cancelled) return
        const [jobsRes, savedRes, appsRes, industriesRes, companySizesRes, workspaceType, businessModel, companyMarket, businessStage] = await Promise.all([
          searchJobs({ companyId: company._id, limit: 50 }),
          getSavedJobs().catch(() => ({ items: [] as SavedJob[] })),
          getApplications().catch(() => ({ items: [] as Application[] })),
          getIndustries(),
          getLookup('companySizes'),
          getLookup('workspaceTypes'),
          getLookup('businessModel'),
          getLookup('companyMarket'),
          getLookup('companyBusinessStage'),
        ])
        if (cancelled) return
        const cityIds = Array.from(
          new Set([
            ...(company.location?.cityId !== undefined ? [company.location.cityId] : []),
            ...jobsRes.items.flatMap((j) => (j.location?.cityId !== undefined ? [j.location.cityId] : [])),
          ]),
        )
        const cities = cityIds.length ? await getCities({ ids: cityIds }).catch(() => []) : []
        if (cancelled) return
        setRealCompany(company)
        setRealJobs(jobsRes.items)
        setSavedJobs(savedRes.items)
        setApplications(appsRes.items)
        setIndustries(industriesRes)
        setCompanySizes(companySizesRes)
        setWorkspaceTypes(workspaceType)
        setBusinessModelLookup(businessModel)
        setCompanyMarketLookup(companyMarket)
        setBusinessStageLookup(businessStage)
        setCitiesById(Object.fromEntries(cities.map((c) => [c.cityId, c])))
      })
      .catch(() => setNotFoundReal(true))
    return () => {
      cancelled = true
    }
  }, [companySlug])

  function snapshotFor(job: JobSearchItem) {
    return {
      jobId: job._id,
      companyId: job.companyId,
      ...(realCompany && { companyData: { name: realCompany.name, ...(realCompany.logoUrl && { logoUrl: realCompany.logoUrl }) } }),
      jobData: {
        title: job.title,
        ...(job.location?.cityId !== undefined && { cityId: job.location.cityId }),
        ...(job.location?.country && { country: job.location.country }),
      },
    }
  }

  async function handleApply(job: JobSearchItem) {
    if (applications.some((a) => a.jobId === job._id && a.status !== 'withdrawn')) return
    const { application } = await applyToJob(snapshotFor(job)).catch(() => ({ application: undefined }))
    if (application) setApplications((current) => [application, ...current])
  }

  async function handleSave(job: JobSearchItem) {
    const already = savedJobs.some((s) => s.jobId === job._id)
    if (already) return
    const { savedJob } = await saveJob(snapshotFor(job)).catch(() => ({ savedJob: undefined }))
    if (savedJob) setSavedJobs((current) => [savedJob, ...current])
  }

  if (realCompany) {
    const locationText = formatLocation(citiesById[realCompany.location?.cityId ?? -1], realCompany.location?.country)
    const industryLabels = labelsFor(industries, realCompany.industries)
    // Prefers the exact headcount when the migration carried one; falls back to the bucket
    // (e.g. "200-500") only when it didn't — never shows both, and shows neither rather than a
    // fabricated number.
    const employeesText = realCompany.numberOfEmployees
      ? `${realCompany.numberOfEmployees.toLocaleString()} Employees`
      : (() => {
          const bucket = labelFor(companySizes, realCompany.sizeId)
          return bucket ? `${bucket} Employees` : undefined
        })()
    // `type` doesn't reliably resolve against any lookup category on real rows (see
    // `CompanyDetail`'s own comment) — found by URL instead of by that field.
    const linkedinUrl = realCompany.contact?.socialLinks?.find((link) => link.url.includes('linkedin.com'))?.url
    const marketLabels = labelsFor(companyMarketLookup, realCompany.companyMarket)
    const businessModelLabels = labelsFor(businessModelLookup, realCompany.businessModel)
    const businessStageLabel = labelFor(businessStageLookup, realCompany.businessStage)
    const hasBusinessInfo = Boolean(industryLabels.length || marketLabels.length || businessModelLabels.length || businessStageLabel)
    const specialtyValues = (realCompany.specialties ?? []).map((s) => s.value)
    const logoSrc = companyLogoUrl(realCompany.logoUrl)

    return (
      <main className="mx-auto max-w-[1200px] px-4 py-8 sm:px-6 md:py-12 lg:px-8">
        <Link to="/jobs" className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent"><ArrowLeft className="size-4" /> Back to jobs</Link>
        <Card className="overflow-hidden rounded-[20px]">
          <CardContent className="p-6 sm:p-9">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
              <Avatar {...(logoSrc ? { src: logoSrc } : {})} name={realCompany.name} className="size-20 shrink-0 text-2xl" />
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-semibold text-primary">{realCompany.name}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-[#555]">
                  {employeesText ? <span className="flex items-center gap-1.5"><Users className="size-4 text-accent" /> {employeesText}</span> : null}
                  {realCompany.foundedYear ? <span className="flex items-center gap-1.5"><Calendar className="size-4 text-accent" /> {realCompany.foundedYear}</span> : null}
                  {locationText ? <span className="flex items-center gap-1.5"><MapPin className="size-4 text-accent" /> {locationText}</span> : null}
                </div>
                {linkedinUrl ? (
                  <a href={linkedinUrl} target="_blank" rel="noreferrer" aria-label={`${realCompany.name} on LinkedIn`} className="mt-3 inline-flex size-8 items-center justify-center rounded-full bg-[#f1f4ff] text-primary hover:bg-accent/15">
                    <Link2 className="size-4" />
                  </a>
                ) : null}
              </div>
              {realCompany.contact?.website ? <Button asChild variant="outline" className="sm:ml-auto"><a href={realCompany.contact.website} target="_blank" rel="noreferrer"><ExternalLink className="size-4" /> Visit website</a></Button> : null}
            </div>

            <div className="mt-7 flex gap-6 border-b border-border">
              <button type="button" onClick={() => setTab('about')} className={`border-b-2 pb-3 text-sm font-semibold transition ${tab === 'about' ? 'border-accent text-primary' : 'border-transparent text-muted-foreground hover:text-primary'}`}>About Us</button>
              <button type="button" onClick={() => setTab('jobs')} className={`border-b-2 pb-3 text-sm font-semibold transition ${tab === 'jobs' ? 'border-accent text-primary' : 'border-transparent text-muted-foreground hover:text-primary'}`}>Open Roles ({realJobs.length})</button>
              <LockedTab label="News" />
              <LockedTab label="Gallery" />
            </div>

            {tab === 'about' ? (
              <div className="mt-7 grid gap-8 lg:grid-cols-[1fr_280px]">
                <div className="min-w-0">
                  {realCompany.description ? <><h2 className="text-lg font-semibold text-primary">About Us</h2><p className="mt-3 max-w-3xl leading-7 text-[#555]">{realCompany.description}</p></> : null}
                  {specialtyValues.length ? <div className="mt-5 flex flex-wrap gap-2">{specialtyValues.map((value) => <Badge key={value} variant="muted">{value}</Badge>)}</div> : null}
                  {!realCompany.description && !specialtyValues.length ? <p className="text-sm text-muted-foreground">No company description yet.</p> : null}
                </div>
                <div className="grid gap-4">
                  {realCompany.contact?.website ? (
                    <section>
                      <h3 className="text-sm font-semibold text-primary">Contact Info</h3>
                      <a href={realCompany.contact.website} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-2 text-sm text-accent hover:underline"><ExternalLink className="size-4" /> {realCompany.contact.website}</a>
                    </section>
                  ) : null}
                  {hasBusinessInfo ? (
                    <section>
                      <h3 className="text-sm font-semibold text-primary">Business Info</h3>
                      <dl className="mt-2 grid gap-2.5 text-sm">
                        {industryLabels.length ? <InfoRow label="Industries" value={industryLabels.join(', ')} /> : null}
                        {marketLabels.length ? <InfoRow label="Market type" value={marketLabels.join(', ')} /> : null}
                        {businessModelLabels.length ? <InfoRow label="Business Model" value={businessModelLabels.join(', ')} /> : null}
                        {businessStageLabel ? <InfoRow label="Business Stage" value={businessStageLabel} /> : null}
                      </dl>
                    </section>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="mt-7">
                {realJobs.length ? realJobs.map((job) => {
                  const city = job.location?.cityId !== undefined ? citiesById[job.location.cityId] : undefined
                  return (
                    <TalentJobCard
                      key={job._id}
                      title={job.title}
                      company={realCompany.name}
                      location={formatLocation(city, job.location?.country)}
                      workMode={workspaceTypes.find((w) => w.elId === job.workspaceType)?.value ?? ''}
                      posted=""
                      tags={[]}
                      saved={savedJobs.some((s) => s.jobId === job._id)}
                      applied={applications.some((a) => a.jobId === job._id && a.status !== 'withdrawn')}
                      onSave={() => void handleSave(job)}
                      onApply={() => void handleApply(job)}
                      onOpen={() => navigate(`/job/${job.slug}`)}
                    />
                  )
                }) : <p className="text-sm text-muted-foreground">No open roles right now.</p>}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    )
  }

  // Real fetch still pending — avoid a flash of "Company not found" while it's in flight.
  if (companySlug && !notFoundReal) return null

  /**
   * The real fetch resolved to nothing. This used to fall back to `mockCompanies` and render a
   * fixture company page — headcount, website and all — under a slug that names no real company.
   */
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold text-primary">Company not found</h1>
      <p className="mt-3 text-muted-foreground">This company page may have been removed.</p>
      <Button className="mt-6" asChild><Link to="/jobs">Back to jobs</Link></Button>
    </main>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{label}</dt><dd className="text-right font-medium text-[#404040]">{value}</dd></div>
}

/** No News/Gallery model exists on this rebuild — a real tab here would have nowhere to go.
 * Shown disabled, matching the reference design, rather than either faking content or omitting
 * the tab (which would silently promise less than the reference's own layout does). */
function LockedTab({ label }: { label: string }) {
  return (
    <button type="button" disabled aria-disabled="true" title="Not available yet" className="flex cursor-not-allowed items-center gap-1.5 border-b-2 border-transparent pb-3 text-sm font-semibold text-muted-foreground/60">
      {label} <Lock className="size-3.5" />
    </button>
  )
}
